import { apiGet, currentSession } from './client'
import type { Envelope } from './types'

type Page = { preview_url?: string | null; page_ref?: string; source_revision_id?: string; source_page_ref?: string }
type Revision = { preview_pages?: Page[] }
type Batch = { until: number; promise: Promise<Page[]> }
const batches = new Map<string, Batch>()
let active = 0
const waiting: (() => void)[] = []
async function limited<T>(task: () => Promise<T>): Promise<T> {
  if (active >= 4) await new Promise<void>(resolve => waiting.push(resolve))
  else active++
  try { return await task() } finally {
    const next = waiting.shift()
    if (next) next()
    else active--
  }
}

/** Only fetch existing, authenticated business endpoints. Never sign a key supplied by the browser. */
export async function refreshPreviewUrl(value: string, force = false): Promise<string | null> {
  let url: URL
  try { url = new URL(value, window.location.origin) } catch { return null }
  if(url.pathname==='/api/v1/material-content'){
    const ticket=url.searchParams.get('ticket');if(!ticket)return null;
    const result=await limited(()=>apiGet<Envelope<{url:string}>>('/material-content-link',{ticket}));return result.data.url;
  }
  const asset = /^\/project-sr\/(?:private|public)\/asset\/([^/]+)\/([^/]+)\/(?:(?:pages|pages-v2)\/)?p\d+\.(?:png|webp)$/.exec(url.pathname)
  const material = /^\/project-sr\/private\/project\/([^/]+)\/(pfl_[^-\/]+)-[^/]+\.preview\/pages-v1\/p\d+\.(?:png|webp)$/.exec(url.pathname)
    ?? /^\/project-sr\/private\/project\/([^/]+)\/(pfl_[^-\/]+)-[^/]+\.image\/[a-f0-9]{64}$/.exec(url.pathname)
  if (!asset && !material) return null
  const path = asset ? `/assets/${asset[1]}/revisions` : `/projects/${material![1]}/materials/${material![2]}/reference`
  const query = asset ? { revision_id: asset[2]!, limit: 1 } : undefined
  // A short, bounded cache coalesces errors from every page in a deck, including
  // lazy images. Session identity prevents reuse after switching accounts.
  const session = currentSession()
  if (!session) return null
  const key = `${session.sessionId}:${path}:${asset?.[2] ?? ''}`
  const now = Date.now()
  for (const [id, batch] of batches) if (batch.until < now) batches.delete(id)
  let batch = batches.get(key)
  if (!batch || force && batch.until !== Infinity) {
    const promise = limited(async () => {
      const result = await apiGet<Envelope<Revision[] | Revision>>(path, query)
      const rows = Array.isArray(result.data) ? result.data : [result.data]
      return rows.flatMap(item => item.preview_pages ?? [])
    })
    batch = { until: Infinity, promise }
    batches.set(key, batch)
    const entry = batch
    void promise.then(() => { entry.until = Date.now() + 20_000 }, () => { if (batches.get(key) === entry) batches.delete(key) })
  }
  const pages = await batch.promise
  if (currentSession()?.sessionId !== session.sessionId) return null
  const sameImage = (candidate: string) => new URL(candidate, window.location.origin).pathname.replace(/\.(png|webp)$/, '') === url.pathname.replace(/\.(png|webp)$/, '')
  const exact = pages.find(page => {
    try { return page.preview_url && sameImage(page.preview_url) } catch { return false }
  })
  if (exact?.preview_url) return exact.preview_url
  // A raw revision can become published while this editor stays open. Its
  // fresh response may then use the CDN, with a different object path.
  const ref = /\/(p\d+)\.(?:png|webp)$/.exec(url.pathname)?.[1]
  return asset ? pages.find(page => url.pathname.includes('/public/')
    ? page.page_ref === ref
    : page.source_revision_id === asset[2] && page.source_page_ref === ref)?.preview_url ?? null : null
}
